import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
} from '@lifeomic/chroma-react/styles';
import Survey, { FieldValueUpdate } from '../util/Survey';
import {
  FieldUpdateContext,
  SurveyContext,
  SurveyResponseContext,
  IsEnabledContext,
  IsReadOnlyContext,
  SubjectIdContext,
} from './Contexts';
import SurveyForm from './SurveyForm';
import PaginatedSurveyForm from './PaginatedSurveyForm';
import SurveyResponse from '../util/SurveyResponse';
import { DialogText } from './SubmitButton';
import SurveyHeader from './SurveyHeader';
import clsx from 'clsx';
import { SurveyOverrideClasses } from './SurveyOverrideClasses';
import { getTestProps } from '../../../util/testProps';
import SurveyItemRefsProvider from './SurveyItemRefsProvider';
import WarnOnRedirect from '../../WarnOnRedirect';
import { debounce } from 'lodash';
import moment from 'moment';
import { useActiveAccountId } from '../../../hooks/useActiveAccountId';
import { v4 } from 'uuid';
import {
  canViewTemplatePreview,
  SurveyTemplatePreview,
} from './SurveyTemplatePreview';
import { evaluatedPolicySelectors } from '../../../redux/modules/evaluatedPolicy';
import { useSelector } from 'react-redux';
import { Policy } from '../../../util/abac';

export const testIds = {
  surveyRoot: 'survey-surveyRoot',
};

const styles = () =>
  createStyles({
    root: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'row',
      alignItems: 'flex-start',
      height: '100%',
      maxWidth: 1200,
      margin: '0 auto',
      gap: 16,
    },
    contentRoot: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      height: '100%',
    },
  });

export interface SurveyComponentOwnProps {
  dialogText?: DialogText;
  secondaryTitle?: string;
  isReadOnly: boolean;
  questionnaireResponse: fhir.QuestionnaireResponse;
  questionnaire: fhir.Questionnaire;
  onResponseUpdate: (
    updatedQuestionnaireRespone: any,
    isValidFhirResource: boolean,
  ) => Promise<void> | void;
  onSaveDraft?: (
    updatedQuestionnaireResponse: fhir.QuestionnaireResponse,
  ) => void;
  onComplete: (isAutoSave?: boolean, onSaveComplete?: () => void) => void;
  defaultStartPageLink?: string;
  onClose?: () => void;
  nonSubjectAuthor?: { reference?: string; display?: string };
  overrideClasses?: SurveyOverrideClasses;
  className?: string;
  autopopulateValues?: FieldValueUpdate[];
  subjectId?: string;
  autoSave?: boolean;
  activeAccount?: string;
  policy?: Policy;
  // Survey option AllowsResponseResubmission can allow further edits to a completed response
  // but we also want to control which context this is permitted in
  // which is the purpose of this prop
  useAllowsResponseResubmission?: boolean;
  templatePreviewEnabled?: boolean;
}

export type SurveyComponentProps = SurveyComponentOwnProps &
  WithStyles<typeof styles>;

export interface SurveyComponentState {
  survey: Survey;
  currentPageLink: string;
  autosaveTimestamp?: string;
  surveyInReadOnlyMode: boolean;
}

const defaultOverride = {} as SurveyOverrideClasses;

export const buildDefaultQuestionnaireResponse = (
  questionnaire: fhir.Questionnaire,
  nonSubjectAuthor?: { reference: string; display: string },
  autopopulateValues: FieldValueUpdate[] = [],
): fhir.QuestionnaireResponse => {
  const asSurvey = new Survey(questionnaire);
  let defaultResponse = asSurvey.defaultResponseWithInitialValues(
    null,
    asSurvey.allRepeatingItems,
    autopopulateValues,
  );

  if (nonSubjectAuthor) {
    defaultResponse = defaultResponse.setAuthor(nonSubjectAuthor);
  }
  return defaultResponse.toFhirQuestionnaireResponse();
};

class SurveyComponent extends PureComponent<
  SurveyComponentProps,
  SurveyComponentState
> {
  state: SurveyComponentState;
  // A default id to use when saving the response
  defaultResponseId = v4();

  constructor(props: SurveyComponentProps) {
    super(props);

    const {
      questionnaire,
      isReadOnly,
      defaultStartPageLink,
      useAllowsResponseResubmission,
    } = props;

    const survey = new Survey(questionnaire);

    const surveyResponse = this._getSurveyResponse(survey);
    if (!this.props.questionnaireResponse) {
      // this was just initialized, update parent
      this._safeUpdateResponse(surveyResponse, true);
    }

    const surveyInReadOnlyMode =
      isReadOnly ||
      (surveyResponse.isComplete &&
        !(survey.allowsResponseResubmission && useAllowsResponseResubmission));

    let currentPageLink = null;

    if (defaultStartPageLink) {
      // default start page can be explictely defined.  useful for survey builder preview
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      currentPageLink = defaultStartPageLink;
    } else if (
      survey.isPaginatedSurvey &&
      surveyResponse.lastVisitedPage &&
      !surveyInReadOnlyMode
    ) {
      // resume where the user last left off if this is a paginated survey and not in read only mode
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      currentPageLink = surveyResponse.lastVisitedPage;
    } else if (survey.isPaginatedSurvey) {
      // there is no "last visited page" on the response, default it
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      currentPageLink = survey.getDefaultSurveyPagination().curLink || null;
    }

    if (!isReadOnly && surveyResponse.isComplete) {
      console.error(
        'Survey set as editable, but survey response is complete.  Component will force read only mode',
      );
    }

    this.state = {
      survey,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      currentPageLink,
      surveyInReadOnlyMode,
    };
  }

  /**
   * The input to this component is a fhir questionniareResponse, but the implementation
   * uses a wrapper SurveyResponse object.  This generates that wrapper
   * @memberof SurveyComponent
   */
  _getSurveyResponse = (survey: Survey = this.state.survey): SurveyResponse => {
    const questionnaireResponse = this.props.questionnaireResponse;
    // if the response has not yet been started, be sure to initialize a response with default value
    const responseStarted = !SurveyResponse.isQuestionnaireResponseNotStarted(
      questionnaireResponse,
    );

    if (responseStarted) {
      return SurveyResponse.initFromFhirQuestionnaireResponse(
        questionnaireResponse,
        survey.allRepeatingItems,
      );
    } else {
      let defaultSurvey = survey.defaultResponseWithInitialValues(
        questionnaireResponse,
        survey.allRepeatingItems,
        this.props.autopopulateValues,
      );

      if (this.props.nonSubjectAuthor) {
        defaultSurvey = defaultSurvey.setAuthor(this.props.nonSubjectAuthor);
      }
      return defaultSurvey;
    }
  };

  _handlePageChange = (isBack: boolean) => {
    const { survey, currentPageLink } = this.state;

    if (!survey.isPaginatedSurvey) {
      throw new Error(
        'Attempted a pagination operation on a non-paginated survey',
      );
    }

    const surveyResponse = this._getSurveyResponse();
    const pagination = survey.getSurveyPaginationForLink(
      currentPageLink,
      surveyResponse,
    );
    const newPageLink = isBack ? pagination.prevLink : pagination.nextLink;

    /*
      if this is a paginated survey, update the response with the last visited page.
      This allows user to resume where they left off in case they close the survey
    */
    const updatedSurveyResponse =
      surveyResponse.updateLastVisitedPage(newPageLink);

    // any page change should require a valid form
    this._safeUpdateResponse(updatedSurveyResponse, true);

    this.setState({
      currentPageLink: newPageLink,
    });
  };

  _safeUpdateResponse = (
    response: SurveyResponse,
    isValidFhirResource: boolean,
  ) => {
    if (this.props.isReadOnly) return;

    if (!this.props.onResponseUpdate) {
      throw new Error(
        '"onResponseUpdate" handler required for a non-read only survey',
      );
    }

    // If the response does not have an id, use the default id to ensure
    // the response isn't saved twice with different ids
    if (!response.id && response.responseWithoutItem) {
      response.responseWithoutItem.id = this.defaultResponseId;
    }

    return this.props.onResponseUpdate(
      response.toFhirQuestionnaireResponse(),
      isValidFhirResource,
    );
  };

  handleNextSurveyPage = () => {
    this._handlePageChange(false);
  };

  handleSaveProgress = (isAutoSave?: boolean) => {
    if (!isAutoSave) {
      // A new save is requested by the user, cancel any pending auto save
      this.autoSaveResponse.cancel();
    }

    const updatedResponse = this._getSurveyResponse().setAsInProgress();

    Promise.resolve().then(async () => {
      await this._safeUpdateResponse(updatedResponse, true);

      this.props.onComplete?.(isAutoSave, () => {
        this.setState({
          autosaveTimestamp: moment().format('hh:mm:ss A'),
        });
      });
    });
  };

  handleCompletion = () => {
    Promise.resolve().then(async () => {
      const updatedResponse = this._getSurveyResponse().setAsComplete();

      await this._safeUpdateResponse(updatedResponse, true);

      this.props.onComplete?.();
    });
  };

  handleSaveDraft = () => {
    if (this.props.onSaveDraft) {
      const updatedResponse = this._getSurveyResponse();
      this.props.onSaveDraft(updatedResponse.toFhirQuestionnaireResponse());
    }
  };

  handlePrevSurveyPage = () => {
    this._handlePageChange(true);
  };

  autoSaveResponse = debounce(() => {
    if (this.props.autoSave && !this.props.isReadOnly) {
      this.handleSaveProgress(true);
    }
  }, 3000);

  handleFieldUpdate = async (fieldUpdates: FieldValueUpdate[]) => {
    const responseUpdates =
      this.state.survey.fieldUpdatesToResponseUpdates(fieldUpdates);
    const modifiedResponses = responseUpdates.filter(
      update =>
        this._getSurveyResponse().getValueAtPath(update.updatePath) !==
        update.value,
    );
    const updatedResponse =
      this._getSurveyResponse().updateFields(responseUpdates);

    /*
      This is triggered for all updates.  This update may make a form (and questionnaireResponse) invalid.
      Assume that this is not a valid form and enhance in the future to check for validity on each update.
    */
    await this._safeUpdateResponse(updatedResponse, false);

    if (modifiedResponses.length > 0) {
      this.autoSaveResponse();
    }
    // If the response has not been saved, attachments will not be able
    // to be uploaded because they require the response id to exist. This
    // will flush the auto save to ensure the response is saved immediately
    // so the attachment field can update the value and create the attachment.
    if (
      !updatedResponse.id &&
      modifiedResponses.some(u => u.valueType === 'valueAttachment')
    ) {
      this.autoSaveResponse.flush();
    }
  };

  render() {
    const {
      classes,
      dialogText,
      onClose,
      secondaryTitle,
      className,
      overrideClasses = defaultOverride,
      onSaveDraft,
      templatePreviewEnabled,
      policy,
    } = this.props;

    const { survey, currentPageLink, surveyInReadOnlyMode } = this.state;

    const surveyResponse = this._getSurveyResponse();
    const surveyPagination = survey.getSurveyPaginationForLink(
      currentPageLink,
      surveyResponse,
    );

    const currentPageSurveyItem = survey.getSurveyPageWithLink(currentPageLink);

    return (
      <div
        className={clsx(classes.root, className)}
        {...getTestProps(testIds.surveyRoot)}
      >
        {/* Make sure a user doesn't navigate to a markdown link without warning */}
        <WarnOnRedirect />
        <div
          className={clsx(classes.contentRoot, overrideClasses.surveyContainer)}
        >
          <SurveyHeader
            onClose={onClose}
            survey={survey}
            currentPageLink={currentPageLink}
            secondaryTitle={secondaryTitle}
          />
          <SurveyContext.Provider value={this.state.survey}>
            <SurveyItemRefsProvider>
              <FieldUpdateContext.Provider value={this.handleFieldUpdate}>
                <SurveyResponseContext.Provider value={surveyResponse}>
                  <IsEnabledContext.Provider value={true}>
                    {/* Completed surveys force read only */}
                    <IsReadOnlyContext.Provider value={surveyInReadOnlyMode}>
                      {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
                      <SubjectIdContext.Provider value={this.props.subjectId}>
                        {
                          survey.isPaginatedSurvey ? (
                            <PaginatedSurveyForm
                              overrideClasses={overrideClasses}
                              dialogText={dialogText}
                              key={currentPageLink}
                              onNextPage={this.handleNextSurveyPage}
                              hasNextPage={surveyPagination.hasNext}
                              onPrevPage={this.handlePrevSurveyPage}
                              hasPrevPage={surveyPagination.hasPrev}
                              prevPageDisabled={surveyPagination.getPrevPageDisabled(
                                surveyInReadOnlyMode,
                              )}
                              enforcePageCheckpoint={surveyPagination.shouldEnforcePageAsCheckpoint(
                                surveyInReadOnlyMode,
                              )}
                              surveyItem={currentPageSurveyItem}
                              onCompletion={this.handleCompletion}
                              onSaveProgress={this.handleSaveProgress}
                              onSaveDraft={onSaveDraft}
                              autosaveTimestamp={this.state.autosaveTimestamp}
                            />
                          ) : (
                            <SurveyForm
                              overrideClasses={overrideClasses}
                              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                              dialogText={dialogText}
                              onCompletion={this.handleCompletion}
                              onSaveProgress={this.handleSaveProgress}
                            />
                          ) // non-paginated survey not yet supported
                        }
                      </SubjectIdContext.Provider>
                    </IsReadOnlyContext.Provider>
                  </IsEnabledContext.Provider>
                </SurveyResponseContext.Provider>
              </FieldUpdateContext.Provider>
            </SurveyItemRefsProvider>
          </SurveyContext.Provider>
        </div>
        {templatePreviewEnabled && policy && canViewTemplatePreview(policy) && (
          <SurveyTemplatePreview
            response={this._getSurveyResponse()}
            surveyId={survey.id}
          />
        )}
      </div>
    );
  }
}
const withStateValues =
  <P extends {}>(WrappedComponent: React.ComponentType<P>) =>
  (props: P) => {
    const accountId = useActiveAccountId();
    const policy = useSelector(evaluatedPolicySelectors.selectPolicyForUser);
    return (
      <WrappedComponent {...props} activeAccount={accountId} policy={policy} />
    );
  };

export default withStyles(styles)(withStateValues(SurveyComponent));
